import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchStudentReports } from "../../redux/admin/studentReport.thunk";
import { fetchStudents } from "../../redux/admin/student.thunk";
import {
  clearSingleStudentState,
  setCurrentPage,
} from "../../redux/admin/studentReport.slice";
import {
  selectStudentsSummary,
  selectSelectedStudentProfile,
  selectSummaryStats,
  selectSessions,
  selectReportType,
  selectReportLoading,
  selectReportError,
  selectReportCurrentPage,
  selectReportTotalPages,
  selectReportTotal,
  selectReportLimit,
  selectIsSingleStudentReport,
} from "../../redux/admin/studentReport.selector";
import { apiCall } from "../../api/core/apiCall";

import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* =====================================================
   SEARCHABLE ACTIVE STUDENT COMBOBOX (ACCESSIBILITY FIXED)
===================================================== */
const StudentCombobox = ({ students = [], value, onSelect, disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const optionRefs = useRef([]);

  const listboxId = "student-combobox-listbox";

  const selectedStudent = useMemo(
    () => students.find((s) => String(s.id) === String(value)),
    [students, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) => {
      const fullName = `${s.name || ""} ${s.last_name || ""}`.toLowerCase();
      const admNo = String(s.admission_number || "").toLowerCase();
      return fullName.includes(q) || admNo.includes(q);
    });
  }, [students, query]);

  useEffect(() => {
    optionRefs.current = optionRefs.current.slice(0, filtered.length);
  }, [filtered.length]);

  const selectedLabel = (student) =>
    student ? `${student.admission_number} — ${student.name} ${student.last_name || ""}`.trim() : "";

  useEffect(() => {
    if (!open) {
      setQuery(selectedLabel(selectedStudent));
    }
  }, [selectedStudent, open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  useEffect(() => {
    if (!open) return;
    const activeEl = optionRefs.current[activeIndex];
    if (activeEl) activeEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeIndex, open]);

  const commitSelection = (student) => {
    onSelect(student ? String(student.id) : "");
    setOpen(false);
    setQuery(student ? selectedLabel(student) : "");
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[activeIndex]) commitSelection(filtered[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery(selectedLabel(selectedStudent));
    }
  };

  return (
    <div className="mnc-combobox-wrapper" ref={wrapperRef}>
      <div
        className={`mnc-combobox-trigger ${open ? "is-active" : ""} ${disabled ? "is-disabled" : ""}`}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) {
            setOpen(true);
            inputRef.current?.focus();
          }
        }}
      >
        <svg className="mnc-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none">
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
          <path d="M21 21l-3.8-3.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>

        <input
          ref={inputRef}
          type="text"
          className="mnc-combobox-input"
          placeholder="Type name or admission number…"
          value={query}
          disabled={disabled}
          onFocus={() => {
            setOpen(true);
            if (selectedStudent) setQuery("");
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
        />

        <div className="mnc-combobox-actions">
          {value && (
            <button
              type="button"
              className="mnc-clear-btn"
              aria-label="Clear selection"
              onClick={(e) => {
                e.stopPropagation();
                commitSelection(null);
              }}
            >
              ×
            </button>
          )}
          <svg
            className={`mnc-chevron ${open ? "is-open" : ""}`}
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      {open && (
        <div className="mnc-combobox-panel">
          <ul id={listboxId} className="mnc-combobox-list" ref={listRef} role="listbox">
            <li
              role="option"
              aria-selected={!value}
              className={`mnc-combobox-item mnc-item-overview ${!value ? "is-selected" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                commitSelection(null);
              }}
            >
              <span className="mnc-avatar mnc-avatar-overview">⚡</span>
              <span className="mnc-item-text">
                <span className="mnc-item-title text-primary">All Active Students Overview</span>
                <span className="mnc-item-sub">Aggregate metrics across active enrollments</span>
              </span>
              {!value && (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </li>

            <li className="mnc-divider" aria-hidden="true" />

            {filtered.length === 0 ? (
              <li className="mnc-empty-item">No active student matches "{query}"</li>
            ) : (
              filtered.map((student, index) => {
                const isSelected = String(student.id) === String(value);
                return (
                  <li
                    key={student.id}
                    ref={(el) => (optionRefs.current[index] = el)}
                    role="option"
                    aria-selected={isSelected}
                    className={`mnc-combobox-item ${index === activeIndex ? "is-active" : ""} ${isSelected ? "is-selected" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      commitSelection(student);
                    }}
                    onMouseEnter={() => setActiveIndex(index)}
                  >
                    <span className="mnc-avatar">
                      {student.name?.charAt(0)?.toUpperCase() || "S"}
                    </span>
                    <span className="mnc-item-text">
                      <span className="mnc-item-title">
                        {student.name} {student.last_name || ""}
                      </span>
                      <span className="mnc-item-sub">{student.admission_number}</span>
                    </span>
                    {isSelected && (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </li>
                );
              })
            )}
          </ul>
          {filtered.length > 0 && (
            <div className="mnc-panel-footer">
              Found {filtered.length} active student{filtered.length !== 1 ? "s" : ""}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* =====================================================
   MAIN STUDENT REPORTS COMPONENT
===================================================== */
const StudentReports = () => {
  const dispatch = useDispatch();

  const { students = [] } = useSelector((state) => state.admin?.students || {});

  const activeStudents = useMemo(() => {
    if (!Array.isArray(students)) return [];
    return students.filter((s) => {
      if (!s) return false;
      if (s.status === undefined || s.status === null) return true;
      return (
        s.status === "active" ||
        s.status === 1 ||
        String(s.status).toLowerCase() === "active"
      );
    });
  }, [students]);

  const studentsSummary = useSelector(selectStudentsSummary);
  const selectedStudentProfile = useSelector(selectSelectedStudentProfile);
  const summaryStats = useSelector(selectSummaryStats);
  const sessions = useSelector(selectSessions);
  const reportType = useSelector(selectReportType);
  const loading = useSelector(selectReportLoading);
  const error = useSelector(selectReportError);
  const currentPage = useSelector(selectReportCurrentPage);
  const totalPages = useSelector(selectReportTotalPages);
  const total = useSelector(selectReportTotal);
  const limit = useSelector(selectReportLimit);
  const isSingleStudent = useSelector(selectIsSingleStudentReport);

  const homeworkList = useSelector(
    (state) => state.admin?.studentReport?.homework || []
  );
  const tournamentsList = useSelector(
    (state) => state.admin?.studentReport?.tournaments || []
  );

  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState("tournaments");
  const [expandedTournamentId, setExpandedTournamentId] = useState(null);

  useEffect(() => {
    dispatch(fetchStudents({ page: 1, limit: 1000, status: "active" }));
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toastr.error(error);
    }
  }, [error]);

  const handleGenerateReport = useCallback(
    (studentIdOverride, pageOverride) => {
      const targetStudentId = studentIdOverride !== undefined ? studentIdOverride : selectedStudentId;
      const pageToFetch = pageOverride || (targetStudentId ? 1 : currentPage);

      dispatch(
        fetchStudentReports({
          studentId: targetStudentId || null,
          fromDate,
          toDate,
          page: pageToFetch,
          limit,
        })
      )
        .unwrap()
        .then(() => {
          toastr.success("Academic report compiled.");
        })
        .catch((err) => {
          toastr.error(err || "Failed to compile report.");
        });
    },
    [dispatch, selectedStudentId, fromDate, toDate, currentPage, limit]
  );

  const handleStudentSelect = (studentId) => {
    setSelectedStudentId(studentId);
    if (studentId) {
      handleGenerateReport(studentId, 1);
    } else {
      dispatch(clearSingleStudentState());
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedStudentId) {
      dispatch(setCurrentPage(1));
    }
    handleGenerateReport(selectedStudentId, 1);
  };

  const handleResetFilters = () => {
    setSelectedStudentId("");
    setFromDate("");
    setToDate("");
    dispatch(setCurrentPage(1));
    dispatch(clearSingleStudentState());
    toastr.info("Filters reset.");
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === currentPage) return;
    dispatch(setCurrentPage(newPage));
    dispatch(
      fetchStudentReports({
        studentId: selectedStudentId || null,
        fromDate,
        toDate,
        page: newPage,
        limit,
      })
    );
  };

  const handleDownloadReport = async () => {
    setDownloading(true);
    try {
      const queryParams = { export_pdf: "1" };
      if (selectedStudentId) queryParams.student_id = selectedStudentId;
      if (fromDate) queryParams.from_date = fromDate;
      if (toDate) queryParams.to_date = toDate;

      const blob = await apiCall(
        "GET",
        "/admin/students/student_reports.php",
        null,
        "Download PDF Report",
        {
          params: queryParams,
          responseType: "blob",
        }
      );

      if (!blob || !(blob instanceof Blob)) {
        toastr.error("Failed to generate PDF file.");
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const fileLabel =
        isSingleStudent && selectedStudentObj
          ? `Report_${selectedStudentObj.admission_number || "Student"}`
          : "Academic_Summary_Report";

      const link = document.createElement("a");
      link.href = url;
      link.download = `${fileLabel}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toastr.success("PDF report exported.");
    } catch (err) {
      toastr.error("Unable to export PDF report.");
    } finally {
      setDownloading(false);
    }
  };

  const selectedStudentObj = useMemo(
    () => activeStudents.find((s) => String(s.id) === String(selectedStudentId)),
    [activeStudents, selectedStudentId]
  );

  return (
    <div className="mnc-app-bg py-3 py-md-4">
      <div className="container-fluid px-2 px-md-4 max-w-1400">

        {/* TOP FILTER CARD */}
        <div className="mnc-card mb-4">
          <div className="p-3 p-md-4 border-bottom bg-white d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <span className="mnc-badge bg-primary-soft text-primary uppercase mb-1 d-inline-block">
                Aadhav Chess Academy • Executive Analytics
              </span>
              <h4 className="mnc-main-title mb-1">Student Performance Portfolio</h4>
              <p className="mnc-sub-title mb-0">
                Track tournament scores, piece win percentages, homework completion, and session logs.
              </p>
            </div>
            <div className="d-flex align-items-center gap-2">
              {isSingleStudent && (
                <button
                  type="button"
                  className="mnc-btn mnc-btn-secondary"
                  onClick={handleResetFilters}
                >
                  ← Clear Selection
                </button>
              )}
              {reportType && (
                <button
                  type="button"
                  className="mnc-btn mnc-btn-primary"
                  onClick={handleDownloadReport}
                  disabled={loading || downloading}
                >
                  {downloading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1"></span>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 3v12"></path>
                        <path d="M7 10l5 5 5-5"></path>
                        <path d="M5 21h14"></path>
                      </svg>
                      Download PDF
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-3 p-md-4 bg-white">
            <div className="row g-3 align-items-end">
              <div className="col-12 col-lg-5">
                <label className="mnc-label mb-1 d-block">Select Active Student Target *</label>
                <StudentCombobox
                  students={activeStudents}
                  value={selectedStudentId}
                  onSelect={handleStudentSelect}
                  disabled={loading}
                />
                {selectedStudentObj && (
                  <div className="mnc-chip mt-2">
                    <span className="mnc-chip-dot" />
                    {selectedStudentObj.name} {selectedStudentObj.last_name || ""} · {selectedStudentObj.admission_number}
                  </div>
                )}
              </div>

              <div className="col-6 col-md-3 col-lg-2">
                <label className="mnc-label mb-1 d-block">From Date</label>
                <input
                  type="date"
                  className="mnc-field"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="col-6 col-md-3 col-lg-2">
                <label className="mnc-label mb-1 d-block">To Date</label>
                <input
                  type="date"
                  className="mnc-field"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="col-12 col-lg-3 d-flex align-items-center gap-2">
                <button
                  type="submit"
                  className="mnc-cta-btn flex-grow-1"
                  disabled={loading}
                >
                  {loading ? (
                    <><span className="spinner-border spinner-border-sm me-1"></span>Compiling...</>
                  ) : (
                    "Compile Analytics"
                  )}
                </button>
                {(selectedStudentId || fromDate || toDate) && (
                  <button
                    type="button"
                    className="mnc-btn mnc-btn-secondary"
                    onClick={handleResetFilters}
                    disabled={loading}
                    style={{ height: "38px" }}
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* LOADING STATE */}
        {loading && (
          <div className="mnc-card text-center py-5 my-4 bg-white">
            <div className="spinner-border text-primary mb-3" style={{ width: "2rem", height: "2rem" }} role="status"></div>
            <p className="fw-600 text-muted mb-0 small">Processing high-density academic and game evaluation logs…</p>
          </div>
        )}

        {/* INITIAL PROMPT */}
        {!loading && !reportType && (
          <div className="mnc-card p-5 text-center bg-white my-4">
            <div className="mnc-hero-circle mx-auto mb-3">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <h6 className="fw-800 text-dark">Select Student Target</h6>
            <p className="text-muted mx-auto" style={{ maxWidth: 420, fontSize: "0.85rem" }}>
              Search and select an active student above to inspect game performance, piece color win rates, and coach recommendations.
            </p>
          </div>
        )}

        {/* =====================================================
            SINGLE STUDENT DETAILED REPORT
        ===================================================== */}
        {!loading && isSingleStudent && (
          <div className="mnc-report-body">

            {/* PROFILE & ANALYTICS HERO */}
            <div className="row g-3 mb-4">
              <div className="col-12 col-md-4">
                <div className="mnc-card p-3 p-md-4 h-100 bg-white">
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <div className="mnc-profile-avatar">
                      {selectedStudentProfile?.name?.charAt(0) || "S"}
                    </div>
                    <div>
                      <h6 className="fw-800 text-dark mb-0">
                        {selectedStudentProfile?.name} {selectedStudentProfile?.last_name || ""}
                      </h6>
                      <span className="mnc-badge bg-light text-secondary border mt-1">
                        Adm: {selectedStudentProfile?.admission_number || "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="pt-3 border-top">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted small">Grade / Level:</span>
                      <strong className="text-dark small">{selectedStudentProfile?.grade || "Standard"}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted small">Mobile Contact:</span>
                      <strong className="text-dark small">{selectedStudentProfile?.mobile_no || "N/A"}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="text-muted small">Account Status:</span>
                      <span className="mnc-badge bg-success-soft text-success uppercase">
                        {selectedStudentProfile?.status || "Active"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* METRICS DASHBOARD */}
              <div className="col-12 col-md-8">
                <div className="mnc-card p-3 p-md-4 h-100 bg-white">
                  <h6 className="mnc-label mb-3">Performance Overview</h6>
                  
                  <div className="row g-2">
                    <div className="col-6 col-sm-3">
                      <div className="mnc-metric-tile">
                        <span className="mnc-tile-accent-line bg-primary" />
                        <span className="mnc-tile-label">Attendance</span>
                        <h4 className="mnc-tile-value text-primary">{summaryStats?.attendance_percentage ?? 0}%</h4>
                        <span className="mnc-tile-sub">{summaryStats?.total_present || 0} Sessions Present</span>
                      </div>
                    </div>

                    <div className="col-6 col-sm-3">
                      <div className="mnc-metric-tile">
                        <span className="mnc-tile-accent-line bg-warning" />
                        <span className="mnc-tile-label">HW Completion</span>
                        <h4 className="mnc-tile-value text-warning">{summaryStats?.homework_completion_rate ?? 0}%</h4>
                        <span className="mnc-tile-sub">{summaryStats?.total_homework || 0} Assigned</span>
                      </div>
                    </div>

                    <div className="col-6 col-sm-3">
                      <div className="mnc-metric-tile">
                        <span className="mnc-tile-accent-line bg-success" />
                        <span className="mnc-tile-label">Tournaments</span>
                        <h4 className="mnc-tile-value text-success">{summaryStats?.total_tournaments || 0}</h4>
                        <span className="mnc-tile-sub">Events Played</span>
                      </div>
                    </div>

                    <div className="col-6 col-sm-3">
                      <div className="mnc-metric-tile">
                        <span className="mnc-tile-accent-line bg-danger" />
                        <span className="mnc-tile-label">Total Score</span>
                        <h4 className="mnc-tile-value text-danger">{summaryStats?.total_tournament_points || 0.0} <small style={{ fontSize: "0.75rem" }}>pts</small></h4>
                        <span className="mnc-tile-sub">Points Scored</span>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* SEGMENTED TAB CONTROLS */}
            <div className="mnc-segmented-tabs mb-3">
              <button
                type="button"
                className={`mnc-segment-btn ${activeTab === "tournaments" ? "is-selected" : ""}`}
                onClick={() => setActiveTab("tournaments")}
              >
                🏆 Tournaments ({tournamentsList.length})
              </button>
              <button
                type="button"
                className={`mnc-segment-btn ${activeTab === "attendance" ? "is-selected" : ""}`}
                onClick={() => setActiveTab("attendance")}
              >
                📅 Sessions ({sessions.length})
              </button>
              <button
                type="button"
                className={`mnc-segment-btn ${activeTab === "homework" ? "is-selected" : ""}`}
                onClick={() => setActiveTab("homework")}
              >
                📝 Homework ({homeworkList.length})
              </button>
            </div>

            {/* TAB 1: TOURNAMENTS (HIGH-PRECISION GAME VIEW) */}
            {activeTab === "tournaments" && (
              <div className="mnc-card mb-4 bg-white">
                <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="fw-800 text-dark mb-0">🏆 Tournament Performance &amp; Game Analytics</h6>
                    <small className="text-muted">Round scores, White/Black win ratios, and individual game reviews</small>
                  </div>
                  <span className="mnc-badge bg-light text-secondary border">{tournamentsList.length} Events Logged</span>
                </div>

                <div className="p-3">
                  {tournamentsList.length === 0 ? (
                    <div className="text-center py-5 text-muted small">
                      No tournament records logged for this student.
                    </div>
                  ) : (
                    tournamentsList.map((t, idx) => {
                      const whiteWinPct = t.white_games_count > 0 ? Math.round((t.white_wins / t.white_games_count) * 100) : 0;
                      const blackWinPct = t.black_games_count > 0 ? Math.round((t.black_wins / t.black_games_count) * 100) : 0;
                      const isExpanded = expandedTournamentId === t.student_tournament_id;

                      return (
                        <div key={t.student_tournament_id || idx} className="mnc-event-card mb-3">
                          <div className="p-3 bg-light-subtle border-bottom d-flex flex-wrap justify-content-between align-items-center gap-2">
                            <div>
                              <div className="d-flex align-items-center gap-2">
                                <h6 className="fw-800 text-dark mb-0">{t.tournament_name}</h6>
                                {t.is_repeated_mistake ? (
                                  <span className="mnc-badge bg-danger-soft text-danger">⚠️ REPEATED ERROR</span>
                                ) : (
                                  <span className="mnc-badge bg-success-soft text-success">✅ IMPROVED</span>
                                )}
                              </div>
                              <small className="text-muted">{t.category} · Played on {t.tournament_date}</small>
                            </div>

                            <div className="d-flex align-items-center gap-2">
                              <span className="mnc-score-pill">
                                Score: {t.final_score} / {t.total_rounds}
                              </span>
                              <button
                                type="button"
                                className="mnc-btn mnc-btn-secondary"
                                onClick={() => setExpandedTournamentId(isExpanded ? null : t.student_tournament_id)}
                              >
                                {isExpanded ? "Hide Games ▲" : "View Games ▼"}
                              </button>
                            </div>
                          </div>

                          <div className="p-3">
                            <div className="row g-3 align-items-center mb-2">
                              <div className="col-12 col-md-4">
                                <div className="p-2 border rounded bg-white">
                                  <div className="d-flex justify-content-between small mb-1">
                                    <span className="fw-700 text-dark">⚪ White Pieces</span>
                                    <span className="fw-700 text-primary">{t.white_wins} / {t.white_games_count} Wins</span>
                                  </div>
                                  <div className="progress" style={{ height: "5px" }}>
                                    <div
                                      className="progress-bar bg-primary"
                                      role="progressbar"
                                      style={{ width: `${whiteWinPct}%` }}
                                    ></div>
                                  </div>
                                </div>
                              </div>

                              <div className="col-12 col-md-4">
                                <div className="p-2 border rounded bg-white">
                                  <div className="d-flex justify-content-between small mb-1">
                                    <span className="fw-700 text-dark">🖤 Black Pieces</span>
                                    <span className="fw-700 text-success">{t.black_wins} / {t.black_games_count} Wins</span>
                                  </div>
                                  <div className="progress" style={{ height: "5px" }}>
                                    <div
                                      className="progress-bar bg-success"
                                      role="progressbar"
                                      style={{ width: `${blackWinPct}%` }}
                                    ></div>
                                  </div>
                                </div>
                              </div>

                              <div className="col-12 col-md-4">
                                <div className="p-2 border rounded bg-light">
                                  <span className="mnc-label d-block mb-1">Coach Assessment</span>
                                  <p className="text-dark small mb-0 font-medium">
                                    {t.overall_coach_summary || "No specific coach summary recorded."}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* EXPANDED HIGH-PRECISION GAME DETAILS */}
                            {isExpanded && (
                              <div className="border-top pt-3 mt-3">
                                <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                                  <span className="mnc-label text-primary">Round-by-Round Game Evaluations</span>
                                  <span className="mnc-badge bg-light text-secondary border fw-600">
                                    {t.games?.length || 0} Rounds Logged
                                  </span>
                                </div>

                                <div className="table-responsive border rounded-8 mnc-subtable-container">
                                  <table className="table mnc-subtable align-middle mb-0">
                                    <thead>
                                      <tr>
                                        <th style={{ width: "45px" }} className="text-center">Rd</th>
                                        <th>Opponent Player</th>
                                        <th className="text-center" style={{ width: "100px" }}>Color</th>
                                        <th className="text-center" style={{ width: "90px" }}>Result</th>
                                        <th>Opening Line</th>
                                        <th className="text-center" style={{ width: "110px" }}>Lichess PGN</th>
                                        <th>Coach Game Review</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {!t.games || t.games.length === 0 ? (
                                        <tr>
                                          <td colSpan="7" className="text-center py-3 text-muted small">
                                            No round game details recorded.
                                          </td>
                                        </tr>
                                      ) : (
                                        t.games.map((g, gIdx) => {
                                          const resultLower = String(g.result || "").toLowerCase();
                                          const isWin = resultLower === "win";
                                          const isLoss = resultLower === "loss";

                                          return (
                                            <tr key={gIdx} className="mnc-game-row">
                                              <td className="text-center fw-800 text-secondary small">R{g.round_number}</td>
                                              <td className="fw-700 text-dark small">{g.opponent_name || "—"}</td>
                                              <td className="text-center">
                                                {String(g.piece_color).toLowerCase() === "white" ? (
                                                  <span className="mnc-piece-badge mnc-piece-white">⚪ White</span>
                                                ) : (
                                                  <span className="mnc-piece-badge mnc-piece-black">⚫ Black</span>
                                                )}
                                              </td>
                                              <td className="text-center">
                                                <span
                                                  className={`mnc-badge ${
                                                    isWin
                                                      ? "bg-success-soft text-success border-success-subtle"
                                                      : isLoss
                                                      ? "bg-danger-soft text-danger border-danger-subtle"
                                                      : "bg-warning-soft text-warning border-warning-subtle"
                                                  }`}
                                                >
                                                  {g.result?.toUpperCase() || "N/A"}
                                                </span>
                                              </td>
                                              <td className="fw-600 text-dark small">{g.opening_name || "Standard Opening"}</td>
                                              <td className="text-center">
                                                {g.lichess_url ? (
                                                  <a
                                                    href={g.lichess_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="mnc-link-btn"
                                                  >
                                                    Study Link ↗
                                                  </a>
                                                ) : (
                                                  <span className="text-muted small">—</span>
                                                )}
                                              </td>
                                              <td className="text-muted small">{g.coach_remarks || "—"}</td>
                                            </tr>
                                          );
                                        })
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* TAB 2: SESSIONS HISTORY (WITH BOOK, CHAPTER, TOPIC & NOTES) */}
            {activeTab === "attendance" && (
              <div className="mnc-card mb-4 bg-white overflow-hidden">
                <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
                  <h6 className="fw-800 text-dark mb-0">Session Attendance &amp; Textbook Coverage</h6>
                  <span className="mnc-badge bg-light text-secondary border">{sessions.length} Sessions</span>
                </div>
                <div className="table-responsive">
                  <table className="table mnc-table align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: "40px" }}>#</th>
                        <th>Date &amp; Time</th>
                        <th>Batch Name</th>
                        <th>Book &amp; Chapter Reference</th>
                        <th>Topic Covered</th>
                        <th className="text-center">Status</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessions.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4 text-muted small">
                            No attendance logs recorded for this period.
                          </td>
                        </tr>
                      ) : (
                        sessions.map((session, idx) => {
                          const statusText = session.attendance_status || session.status || "Absent";
                          const isPresent = String(statusText).trim().toLowerCase() === "present";

                          return (
                            <tr key={session.attendance_id || idx}>
                              <td className="text-muted fw-600">{idx + 1}</td>
                              <td>
                                <div className="fw-700 text-dark">{session.session_date}</div>
                                <small className="text-muted">{session.start_time || ""}</small>
                              </td>
                              <td className="fw-600 text-dark">{session.session_name || "Regular Batch"}</td>
                              <td>
                                {session.book_title ? (
                                  <div>
                                    <div className="fw-700 text-dark" style={{ fontSize: "0.8rem" }}>
                                      {session.book_title}
                                    </div>
                                    {session.chapter_name && (
                                      <div className="text-primary fw-600" style={{ fontSize: "0.72rem", marginTop: "2px" }}>
                                        {session.chapter_no ? `Ch ${session.chapter_no}: ` : ""}
                                        {session.chapter_name}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-muted small italic">No Book Assigned</span>
                                )}
                              </td>
                              <td className="fw-600 text-primary">{session.topic || "General Session"}</td>
                              <td className="text-center">
                                {isPresent ? (
                                  <span className="mnc-badge bg-success-soft text-success">PRESENT</span>
                                ) : (
                                  <span className="mnc-badge bg-danger-soft text-danger">ABSENT</span>
                                )}
                              </td>
                              <td className="text-muted small" style={{ maxWidth: "260px" }}>
                                {session.notes || "—"}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TAB 3: HOMEWORK HISTORY */}
            {activeTab === "homework" && (
              <div className="mnc-card mb-4 bg-white overflow-hidden">
                <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
                  <h6 className="fw-800 text-dark mb-0">Homework Assignments &amp; Submissions</h6>
                  <span className="mnc-badge bg-light text-secondary border">{homeworkList.length} Assignments</span>
                </div>
                <div className="table-responsive">
                  <table className="table mnc-table align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: "40px" }}>#</th>
                        <th>Title &amp; Subject</th>
                        <th>Due Date</th>
                        <th className="text-center">Status</th>
                        <th>Teacher Feedback</th>
                      </tr>
                    </thead>
                    <tbody>
                      {homeworkList.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center py-4 text-muted small">
                            No homework assigned for this period.
                          </td>
                        </tr>
                      ) : (
                        homeworkList.map((hw, idx) => {
                          const status = String(hw.submission_status || "Pending").toLowerCase();

                          return (
                            <tr key={hw.homework_assignment_id || idx}>
                              <td className="text-muted fw-600">{idx + 1}</td>
                              <td>
                                <div className="fw-700 text-dark">{hw.title}</div>
                                <small className="text-muted">{hw.subject}</small>
                              </td>
                              <td className="fw-600 text-dark">{hw.due_date || "N/A"}</td>
                              <td className="text-center">
                                {status === "reviewed" ? (
                                  <span className="mnc-badge bg-success-soft text-success">REVIEWED</span>
                                ) : status === "submitted" ? (
                                  <span className="mnc-badge bg-warning-soft text-warning">SUBMITTED</span>
                                ) : (
                                  <span className="mnc-badge bg-danger-soft text-danger">PENDING</span>
                                )}
                              </td>
                              <td className="text-muted small">{hw.teacher_feedback || hw.student_notes || "—"}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          </div>
        )}

        {/* =====================================================
            ALL STUDENTS AGGREGATE SUMMARY VIEW
        ===================================================== */}
        {!loading && !isSingleStudent && reportType === "all_students_summary" && (
          <div className="mnc-card bg-white overflow-hidden">
            <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
              <div>
                <h6 className="fw-800 text-dark mb-0">Active Students Performance Summary</h6>
                <small className="text-muted">Aggregate attendance, homework, and tournament score metrics</small>
              </div>
              <span className="mnc-badge bg-light text-secondary border">Total: {total} Students</span>
            </div>

            <div className="table-responsive">
              <table className="table mnc-table align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: "40px" }}>#</th>
                    <th>Student Name</th>
                    <th>Admission No</th>
                    <th className="text-center">Sessions</th>
                    <th className="text-center">Att. %</th>
                    <th className="text-center">HW %</th>
                    <th className="text-center">Tournaments</th>
                    <th className="text-center">Total Score</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {studentsSummary.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center py-5 text-muted small">
                        No student report data available.
                      </td>
                    </tr>
                  ) : (
                    studentsSummary.map((item, idx) => {
                      const rowNum = (currentPage - 1) * limit + idx + 1;
                      const attPercentage = Number(item.attendance_percentage || 0);
                      const hwPercentage = Number(item.homework_completion_rate || 0);

                      return (
                        <tr key={item.student_id || idx}>
                          <td className="text-muted fw-600">{rowNum}</td>
                          <td>
                            <div className="fw-700 text-dark">{item.student_name}</div>
                            <small className="text-muted">{item.mobile_no || "Active"}</small>
                          </td>
                          <td className="fw-600 text-secondary">{item.admission_number || "—"}</td>
                          <td className="text-center fw-600">{item.total_sessions || 0}</td>
                          <td className="text-center">
                            <span
                              className={`mnc-badge ${
                                attPercentage >= 75
                                  ? "bg-success-soft text-success"
                                  : attPercentage >= 50
                                  ? "bg-warning-soft text-warning"
                                  : "bg-danger-soft text-danger"
                              }`}
                            >
                              {attPercentage}%
                            </span>
                          </td>
                          <td className="text-center">
                            <span
                              className={`mnc-badge ${
                                hwPercentage >= 75
                                  ? "bg-success-soft text-success"
                                  : hwPercentage >= 50
                                  ? "bg-warning-soft text-warning"
                                  : "bg-danger-soft text-danger"
                              }`}
                            >
                              {hwPercentage}%
                            </span>
                          </td>
                          <td className="text-center fw-600">{item.total_tournaments || 0}</td>
                          <td className="text-center fw-800 text-danger">
                            {item.total_points || 0.0} pts
                          </td>
                          <td className="text-end">
                            <button
                              type="button"
                              className="mnc-btn mnc-btn-secondary"
                              onClick={() => handleStudentSelect(String(item.student_id))}
                            >
                              Details &rarr;
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* PAGINATION */}
            {totalPages > 1 && (
              <div className="p-3 border-top d-flex justify-content-between align-items-center bg-light">
                <span className="small text-muted fw-600">
                  Page {currentPage} of {totalPages} ({total} Total Records)
                </span>
                <div className="btn-group">
                  <button
                    className="mnc-btn mnc-btn-secondary"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </button>
                  <button
                    className="mnc-btn mnc-btn-secondary"
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

      </div>

      {/* REFINED CLEAN CSS STYLES */}
      <style>{`
        :root {
          --mnc-bg: #f8fafc;
          --mnc-surface: #ffffff;
          --mnc-ink: #0f172a;
          --mnc-sub: #64748b;
          --mnc-border: #e2e8f0;
          --mnc-blue: #0284c7;
          --mnc-blue-hover: #0369a1;
          --mnc-blue-soft: #f0f9ff;
          --mnc-green: #16a34a;
          --mnc-green-soft: #f0fdf4;
          --mnc-amber: #d97706;
          --mnc-amber-soft: #fffbeb;
          --mnc-red: #dc2626;
          --mnc-red-soft: #fef2f2;
          --mnc-radius: 12px;
          --mnc-font: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .mnc-app-bg {
          background: var(--mnc-bg);
          min-height: 100vh;
          font-family: var(--mnc-font);
          color: var(--mnc-ink);
        }

        .max-w-1400 { max-width: 1400px; margin: 0 auto; }

        .mnc-card {
          background: var(--mnc-surface);
          border-radius: var(--mnc-radius);
          border: 1px solid var(--mnc-border);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .mnc-event-card {
          background: #ffffff;
          border: 1px solid var(--mnc-border);
          border-radius: 10px;
          overflow: hidden;
        }

        .mnc-main-title { font-size: 1.2rem; font-weight: 800; color: var(--mnc-ink); }
        .mnc-sub-title { font-size: 0.82rem; color: var(--mnc-sub); }
        .mnc-label { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--mnc-sub); }

        .mnc-btn {
          font-size: 0.78rem;
          font-weight: 600;
          height: 34px;
          padding: 0 14px;
          border-radius: 6px;
          transition: all 0.15s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .mnc-btn-primary {
          background: var(--mnc-blue);
          color: #ffffff;
          border: none;
        }
        .mnc-btn-primary:hover:not(:disabled) {
          background: var(--mnc-blue-hover);
        }

        .mnc-btn-secondary {
          background: #ffffff;
          color: var(--mnc-ink);
          border: 1px solid var(--mnc-border);
        }
        .mnc-btn-secondary:hover:not(:disabled) {
          background: #f8fafc;
          border-color: #cbd5e1;
        }

        .mnc-cta-btn {
          background: var(--mnc-blue);
          color: #ffffff;
          border: none;
          border-radius: 8px;
          height: 38px;
          font-weight: 600;
          font-size: 0.82rem;
          transition: background 0.15s ease;
        }
        .mnc-cta-btn:hover:not(:disabled) {
          background: var(--mnc-blue-hover);
        }

        .mnc-badge {
          font-size: 0.62rem;
          font-weight: 700;
          padding: 3px 8px;
          border-radius: 4px;
          letter-spacing: 0.03em;
        }

        .mnc-score-pill {
          font-size: 0.78rem;
          font-weight: 700;
          background: #f1f5f9;
          color: var(--mnc-ink);
          padding: 4px 10px;
          border-radius: 6px;
          border: 1px solid var(--mnc-border);
        }

        .mnc-link-btn {
          font-size: 0.75rem;
          font-weight: 700;
          color: var(--mnc-blue);
          text-decoration: none;
        }
        .mnc-link-btn:hover { text-decoration: underline; }

        .bg-primary-soft { background: var(--mnc-blue-soft); }
        .bg-success-soft { background: var(--mnc-green-soft); }
        .bg-warning-soft { background: var(--mnc-amber-soft); }
        .bg-danger-soft { background: var(--mnc-red-soft); }

        .text-primary { color: var(--mnc-blue) !important; }
        .text-success { color: var(--mnc-green) !important; }
        .text-warning { color: var(--mnc-amber) !important; }
        .text-danger { color: var(--mnc-red) !important; }

        .mnc-metric-tile {
          position: relative;
          background: #f8fafc;
          border: 1px solid var(--mnc-border);
          border-radius: 8px;
          padding: 10px;
          text-align: center;
          overflow: hidden;
        }
        .mnc-tile-accent-line {
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 3px;
        }
        .mnc-tile-label { font-size: 0.65rem; font-weight: 700; text-transform: uppercase; color: var(--mnc-sub); }
        .mnc-tile-value { font-size: 1.25rem; font-weight: 800; margin: 2px 0 0 0; }
        .mnc-tile-sub { font-size: 0.68rem; color: var(--mnc-sub); }

        .mnc-field {
          width: 100%;
          height: 38px;
          border: 1px solid var(--mnc-border);
          border-radius: 8px;
          padding: 0 10px;
          font-size: 0.82rem;
          color: var(--mnc-ink);
          background: #ffffff;
        }

        .mnc-segmented-tabs {
          display: inline-flex;
          background: #e2e8f0;
          padding: 2px;
          border-radius: 8px;
        }
        .mnc-segment-btn {
          border: none;
          background: transparent;
          padding: 6px 14px;
          font-size: 0.78rem;
          font-weight: 700;
          color: var(--mnc-sub);
          border-radius: 6px;
          transition: all 0.15s ease;
        }
        .mnc-segment-btn.is-selected {
          background: #ffffff;
          color: var(--mnc-ink);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        }

        .mnc-table th {
          font-size: 0.68rem;
          font-weight: 700;
          text-transform: uppercase;
          color: var(--mnc-sub);
          background: #f8fafc;
          border-bottom: 1px solid var(--mnc-border);
          padding: 10px 14px;
        }
        .mnc-table td {
          padding: 10px 14px;
          border-bottom: 1px solid #f1f5f9;
          font-size: 0.82rem;
        }

        .mnc-subtable-container {
          background: #ffffff;
          border-color: var(--mnc-border) !important;
        }

        .mnc-subtable {
          border-collapse: separate;
          border-spacing: 0;
        }

        .mnc-subtable th {
          background: #f8fafc !important;
          color: var(--mnc-sub);
          font-size: 0.68rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          padding: 8px 12px;
          border-bottom: 1px solid var(--mnc-border);
        }

        .mnc-subtable td {
          font-size: 0.78rem;
          padding: 8px 12px;
          border-bottom: 1px solid #f1f5f9;
        }

        .mnc-game-row:last-child td { border-bottom: none; }
        .mnc-game-row:hover td { background: #f8fafc; }

        .mnc-piece-badge {
          font-size: 0.68rem;
          font-weight: 700;
          padding: 2px 8px;
          border-radius: 4px;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
        }

        .border-success-subtle { border: 1px solid #bbf7d0; }
        .border-danger-subtle { border: 1px solid #fecaca; }
        .border-warning-subtle { border: 1px solid #fef08a; }

        .rounded-8 { border-radius: 8px; }

        .mnc-combobox-wrapper { position: relative; width: 100%; }
        .mnc-combobox-trigger {
          display: flex;
          align-items: center;
          position: relative;
          border: 1px solid var(--mnc-border);
          border-radius: 8px;
          background: #ffffff;
          height: 38px;
        }
        .mnc-combobox-trigger.is-active { border-color: var(--mnc-blue); box-shadow: 0 0 0 3px var(--mnc-blue-soft); }
        .mnc-search-icon { position: absolute; left: 10px; color: var(--mnc-sub); pointer-events: none; }
        .mnc-combobox-input {
          padding-left: 32px;
          padding-right: 50px;
          border: none;
          height: 100%;
          width: 100%;
          background: transparent;
          font-size: 0.82rem;
        }
        .mnc-combobox-input:focus { outline: none; }
        .mnc-combobox-actions { position: absolute; right: 8px; display: flex; align-items: center; gap: 4px; }
        .mnc-clear-btn {
          border: none;
          background: #e2e8f0;
          border-radius: 50%;
          width: 16px;
          height: 16px;
          font-size: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .mnc-combobox-panel {
          position: absolute;
          z-index: 999999 !important;
          top: calc(100% + 4px);
          left: 0;
          right: 0;
          background: #ffffff;
          border: 1px solid var(--mnc-border);
          border-radius: 8px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .mnc-combobox-list { max-height: 240px; overflow-y: auto; padding: 4px; margin: 0; list-style: none; }
        .mnc-combobox-item { display: flex; align-items: center; gap: 8px; padding: 6px 8px; border-radius: 6px; cursor: pointer; }
        .mnc-combobox-item.is-active { background: var(--mnc-blue-soft); }
        .mnc-avatar { width: 24px; height: 24px; border-radius: 50%; background: #e2e8f0; display: flex; align-items: center; justify-content: center; font-size: 0.7rem; font-weight: 700; }
        .mnc-avatar-overview { background: var(--mnc-blue-soft); color: var(--mnc-blue); }
        .mnc-item-text { display: flex; flex-direction: column; flex: 1; }
        .mnc-item-title { font-size: 0.8rem; font-weight: 600; color: var(--mnc-ink); }
        .mnc-item-sub { font-size: 0.68rem; color: var(--mnc-sub); }
        .mnc-divider { height: 1px; background: var(--mnc-border); margin: 4px 0; }
        .mnc-empty-item { padding: 12px; text-align: center; color: var(--mnc-sub); font-size: 0.8rem; }
        .mnc-panel-footer { padding: 5px 10px; font-size: 0.68rem; color: var(--mnc-sub); background: #f8fafc; border-top: 1px solid var(--mnc-border); font-weight: 600; }

        .mnc-chip { display: inline-flex; align-items: center; gap: 6px; font-size: 0.75rem; font-weight: 600; color: var(--mnc-blue); background: var(--mnc-blue-soft); padding: 3px 8px; border-radius: 6px; }
        .mnc-chip-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--mnc-blue); }

        .mnc-hero-circle { width: 44px; height: 44px; border-radius: 50%; background: var(--mnc-blue-soft); color: var(--mnc-blue); display: flex; align-items: center; justify-content: center; }
        .mnc-profile-avatar { width: 40px; height: 40px; border-radius: 50%; background: var(--mnc-blue-soft); color: var(--mnc-blue); font-weight: 800; display: flex; align-items: center; justify-content: center; font-size: 1rem; }

        @media (max-width: 767.98px) {
          .mnc-segmented-tabs { display: flex; width: 100%; }
          .mnc-segment-btn { flex: 1; text-align: center; font-size: 0.75rem; padding: 6px 4px; }
        }
      `}</style>
    </div>
  );
};

export default StudentReports;